import React, { useEffect, useRef, useState } from 'react';

const BAR_COUNT = 32;
const SAMPLE_EVERY_MS = 70;
const WARN_REMAINING_MS = 30 * 1000;

export function formatElapsed(ms) {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

/**
 * Stands in for the textarea while the mic is live: a pulsing dot, elapsed
 * time and a scrolling level meter so people can see Atlas is hearing them.
 * Bars are written straight to the DOM each frame; React only re-renders for
 * the clock.
 */
export default function VoiceRecordingStrip({ analyser, startedAt, maxMs }) {
  const [now, setNow] = useState(() => Date.now());
  const barsRef = useRef([]);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!analyser) return undefined;
    const samples = new Uint8Array(analyser.fftSize);
    const levels = new Array(BAR_COUNT).fill(0);
    let frame = 0;
    let lastSample = 0;

    const draw = (time) => {
      frame = requestAnimationFrame(draw);
      if (time - lastSample < SAMPLE_EVERY_MS) return;
      lastSample = time;
      analyser.getByteTimeDomainData(samples);
      let sum = 0;
      for (let i = 0; i < samples.length; i += 1) {
        const value = (samples[i] - 128) / 128;
        sum += value * value;
      }
      // Speech RMS sits around 0.02–0.2; sqrt spreads that across the bar.
      const level = Math.min(1, Math.sqrt(Math.sqrt(sum / samples.length)) * 1.6);
      levels.shift();
      levels.push(level);
      barsRef.current.forEach((bar, index) => {
        if (bar) bar.style.transform = `scaleY(${Math.max(0.08, levels[index])})`;
      });
    };
    frame = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(frame);
  }, [analyser]);

  const elapsed = startedAt ? now - startedAt : 0;
  const remaining = maxMs - elapsed;
  const nearLimit = remaining <= WARN_REMAINING_MS;

  return (
    <div className="atlas-voice-strip" role="status" aria-live="off">
      <span className="atlas-voice-dot" aria-hidden="true" />
      <span className={`atlas-voice-clock${nearLimit ? ' atlas-voice-clock-warn' : ''}`}>
        {nearLimit ? `${formatElapsed(remaining)} left` : formatElapsed(elapsed)}
      </span>
      <span className="atlas-voice-bars" aria-hidden="true">
        {Array.from({ length: BAR_COUNT }, (_, index) => (
          <span
            key={index}
            className="atlas-voice-bar"
            ref={(node) => { barsRef.current[index] = node; }}
          />
        ))}
      </span>
      <span className="atlas-voice-label">Listening…</span>
    </div>
  );
}
